"use strict";
/**
 * «Fornitori»: the supplier types, on two levels (decision 255, #983): a macro-category and,
 * optionally, a detail of it. Filtering by a macro-category finds all its details; filtering by a
 * detail finds only that detail.
 *
 * DERIVED FROM THE EXPENSE TYPE. The macro-categories are the expense types a supplier can be, with
 * the same code (`EXPENSE_CATEGORIES`), so an expense's type gives its supplier's macro-category by
 * lookup. The three meal types share one macro-category, `food`: the restaurant is the same
 * supplier whatever the meal was for. The types no supplier is (mileage, the flat rate) and the one
 * that depends on who was paid (travel fees) give none.
 *
 * DETAILS START WITH TRANSPORT AND LODGING (decision 255, point 5). The others are the site
 * categories already in use before #983, kept so no site loses what it said; the rest come later.
 *
 * Every code is unique across both levels, so one `type` filter takes either.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SUPPLIER_TYPE_CODES = exports.SUPPLIER_DETAIL_CODES = exports.SUPPLIER_MACRO_CODES = exports.EXPENSE_CATEGORIES_WITHOUT_SUPPLIER_TYPE = exports.SUPPLIER_MACRO_CATEGORIES = void 0;
exports.supplierMacroByCode = supplierMacroByCode;
exports.supplierMacroOfDetail = supplierMacroOfDetail;
exports.isSupplierDetailOf = isSupplierDetailOf;
exports.supplierMacroForExpenseCategory = supplierMacroForExpenseCategory;
exports.supplierTypeLabel = supplierTypeLabel;
function macro(code, labelIt, labelEn, expenseCategories = [code], details = []) {
    return { code, labelIt, labelEn, expenseCategories, details };
}
exports.SUPPLIER_MACRO_CATEGORIES = [
    macro('transportation', 'Trasporto', 'Transportation', ['transportation'], [
        { code: 'flight', labelIt: 'Volo', labelEn: 'Flight' },
        { code: 'train', labelIt: 'Treno', labelEn: 'Train' },
        { code: 'taxi', labelIt: 'Taxi', labelEn: 'Taxi' },
        { code: 'metro', labelIt: 'Metro', labelEn: 'Metro' },
        { code: 'ncc', labelIt: 'NCC', labelEn: 'Chauffeur (NCC)' },
        { code: 'scooter', labelIt: 'Monopattino', labelEn: 'Scooter' },
        // A site category before #983.
        { code: 'car_rental', labelIt: 'Noleggio auto', labelEn: 'Car rental' },
    ]),
    macro('lodging', 'Alloggio', 'Lodging', ['lodging'], [
        { code: 'hotel', labelIt: 'Hotel', labelEn: 'Hotel' },
        { code: 'b_and_b', labelIt: 'B&B', labelEn: 'B&B' },
        { code: 'apartment', labelIt: 'Appartamento', labelEn: 'Apartment' },
    ]),
    macro('food', 'Ristorazione', 'Food and drink', ['travel-meals', 'entertainment-meals', 'work-meals'], [
        // Site categories before #983.
        { code: 'restaurant', labelIt: 'Ristorante', labelEn: 'Restaurant' },
        { code: 'bar', labelIt: 'Bar', labelEn: 'Bar' },
    ]),
    macro('fuel', 'Carburante', 'Fuel'),
    macro('parking', 'Parcheggio', 'Parking'),
    macro('road-charges', 'Pedaggi stradali', 'Road charges'),
    macro('entertainment', 'Spese di rappresentanza', 'Entertainment'),
    macro('supplies', 'Forniture', 'Supplies', ['supplies'], [
        // Site categories before #983.
        { code: 'copy_shop', labelIt: 'Copisteria', labelEn: 'Copy shop' },
        { code: 'shop', labelIt: 'Negozio', labelEn: 'Shop' },
    ]),
    macro('equipment', 'Attrezzature', 'Equipment'),
    macro('professional-services', 'Servizi professionali', 'Professional services'),
    macro('telecommunications', 'Telecomunicazioni', 'Telecommunications'),
    macro('gifts', 'Regali', 'Gifts'),
    macro('digital-services', 'Servizi digitali', 'Digital services'),
    macro('training', 'Formazione', 'Training'),
    macro('marketing', 'Marketing', 'Marketing'),
    macro('infrastructure', 'Infrastruttura', 'Infrastructure'),
    macro('regulatory-fees', 'Tasse regolatorie', 'Regulatory fees'),
    macro('insurance', 'Assicurazione', 'Insurance'),
    macro('vehicle-expenses', 'Spese per veicoli', 'Vehicle expenses'),
    macro('other', 'Altro', 'Other'),
];
/**
 * The expense types that give no macro-category: no supplier is behind them (mileage, the flat
 * rate), or the supplier's type depends on who was paid (travel fees: an airline, an insurer).
 */
exports.EXPENSE_CATEGORIES_WITHOUT_SUPPLIER_TYPE = [
    'mileage-reimbursement',
    'flat-rate-allowance',
    'travel-fees',
];
exports.SUPPLIER_MACRO_CODES = exports.SUPPLIER_MACRO_CATEGORIES.map((m) => m.code);
exports.SUPPLIER_DETAIL_CODES = exports.SUPPLIER_MACRO_CATEGORIES.flatMap((m) => m.details.map((d) => d.code));
/** Both levels' codes: what a `type` filter accepts. */
exports.SUPPLIER_TYPE_CODES = [
    ...exports.SUPPLIER_MACRO_CODES,
    ...exports.SUPPLIER_DETAIL_CODES,
];
function supplierMacroByCode(code) {
    return exports.SUPPLIER_MACRO_CATEGORIES.find((m) => m.code === code);
}
/** The macro-category a detail belongs to. */
function supplierMacroOfDetail(detailCode) {
    return exports.SUPPLIER_MACRO_CATEGORIES.find((m) => m.details.some((d) => d.code === detailCode));
}
/** Whether `detailCode` is one of `macroCode`'s details. */
function isSupplierDetailOf(macroCode, detailCode) {
    return supplierMacroOfDetail(detailCode)?.code === macroCode;
}
/** The macro-category an expense of this type gives its supplier, or null for none. */
function supplierMacroForExpenseCategory(expenseCategoryCode) {
    return (exports.SUPPLIER_MACRO_CATEGORIES.find((m) => m.expenseCategories.includes(expenseCategoryCode))
        ?.code ?? null);
}
/** A type code's label, either level; the code itself when unknown. */
function supplierTypeLabel(code, language) {
    const found = supplierMacroByCode(code) ??
        exports.SUPPLIER_MACRO_CATEGORIES.flatMap((m) => m.details).find((d) => d.code === code);
    if (found === undefined)
        return code;
    return language === 'it' ? found.labelIt : found.labelEn;
}
//# sourceMappingURL=supplier-types.js.map